import React, { useState, useEffect, useCallback } from 'react';
import { Contract, Customer, ContractLine, Invoice } from '../types';
import { apiService } from '../services/api';
import { isValidDocumentNumber, DOCUMENT_PREFIXES } from '../utils/documentNumber';

// BulkEditInvoiceLines component (extracted for clarity)
interface BulkEditProps {
  editableLines: InvoiceLineEdit[];
  setEditableLines: React.Dispatch<React.SetStateAction<InvoiceLineEdit[]>>;
}

const BulkEditInvoiceLines: React.FC<BulkEditProps> = ({ editableLines, setEditableLines }) => {
  const [bulkPrice, setBulkPrice] = useState<string>('');
  const [bulkTax, setBulkTax] = useState<string>('');

  const handleBulkEdit = () => {
    setEditableLines(editableLines.map(line =>
      (line as any).selected
        ? {
            ...line,
            unit_price: bulkPrice !== '' ? parseFloat(bulkPrice) : line.unit_price,
            tax_rate: bulkTax !== '' ? parseFloat(bulkTax) : line.tax_rate,
            line_total: (bulkPrice !== '' ? parseFloat(bulkPrice) : Number(line.unit_price) || 0) * (Number(line.quantity) || 0),
            tax_amount:
              ((bulkPrice !== '' ? parseFloat(bulkPrice) : Number(line.unit_price) || 0) * (Number(line.quantity) || 0)) *
              ((bulkTax !== '' ? parseFloat(bulkTax) : Number(line.tax_rate) || 0) / 100)
          }
        : line
    ));
  };

  return (
    <div style={{ marginBottom: 10, background: '#f8f9fa', padding: 10, borderRadius: 4 }}>
      <span style={{ marginRight: 10 }}>Bulk Edit Selected Lines:</span>
      <input
        type="number"
        placeholder="Unit Price"
        value={bulkPrice}
        onChange={e => setBulkPrice(e.target.value)}
        style={{ width: 100, marginRight: 10 }}
      />
      <input
        type="number"
        placeholder="Tax Rate %"
        value={bulkTax}
        onChange={e => setBulkTax(e.target.value)}
        style={{ width: 100, marginRight: 10 }}
      />
      <button className="btn btn-info btn-sm" onClick={handleBulkEdit}>
        Apply
      </button>
    </div>
  );
};

interface InvoiceFormProps {
  contract?: Contract | null;
  contracts: Contract[];
  customers: Customer[];
  initialData?: Invoice;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

interface InvoiceLineEdit {
  invoiceline_id?: string;
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  tax_rate: number;
  tax_amount: number;
  selected?: boolean;
}

const InvoiceForm: React.FC<InvoiceFormProps> = ({
  contract,
  contracts,
  customers,
  initialData,
  onSubmit,
  onCancel
}) => {
  const isEditMode = !!initialData;
  // Allow editing for all invoices, not just drafts
  const isDraft = true; // Always allow editing
  
  const [formData, setFormData] = useState({
    invoice_name: initialData?.invoice_name || '',
    contract_id: initialData?.contract_id || contract?.contract_id || '',
    customer_id: initialData?.customer_id || contract?.customer_id || 0,
    invoice_date: initialData?.invoice_date ? new Date(initialData.invoice_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    due_date: initialData?.due_date ? new Date(initialData.due_date).toISOString().split('T')[0] : new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    billing_period_start: initialData?.billing_period_start ? new Date(initialData.billing_period_start).toISOString().split('T')[0] : '',
    billing_period_end: initialData?.billing_period_end ? new Date(initialData.billing_period_end).toISOString().split('T')[0] : '',
    payment_terms: '30 days',
    notes: initialData?.notes || '',
    include_contract_lines: !isEditMode,
    discount_type: 'percentage' as 'percentage' | 'fixed',
    discount_value: 0,
    discount_amount: 0,
    subtotal: initialData?.subtotal || 0,
    tax_amount: initialData?.tax_amount || 0,
    total_amount: initialData?.total_amount || 0,
    currency: initialData?.currency || 'EUR'
  });
  const [userEditedDueDate, setUserEditedDueDate] = useState(false);
  
  // Helper functions to compute billing period from contract
  const computeBillingPeriod = React.useCallback((c: Contract) => {
    const dateOnly = (d: Date) => d.toISOString().split('T')[0];
    const getMonthsFromCycle = (cycle?: Contract['billing_cycle']) => {
      switch (cycle) {
        case '3_months':
          return 3;
        case '12_months':
          return 12;
        case '1_month':
        default:
          return 1;
      }
    };
    try {
      const startISO = c.next_billing_start || c.start_date;
      const start = new Date(startISO);
      const months = getMonthsFromCycle(c.billing_cycle);
      const nextStart = new Date(start);
      nextStart.setMonth(nextStart.getMonth() + months);
      const end = new Date(nextStart);
      end.setDate(end.getDate() - 1);
      return {
        start: dateOnly(start),
        end: dateOnly(end)
      };
    } catch {
      return { start: '', end: '' };
    }
  }, []);

  // Update form data when contract prop changes
  useEffect(() => {
    if (contract) {
      // Validate customer_id from contract
      const customerId = contract.customer_id;
      if (!customerId || customerId <= 0) {
        console.error('Contract has invalid customer_id:', contract);
      }

      // Compute billing period from upcoming start and billing cycle
      const period = computeBillingPeriod(contract);
      
      setFormData(prev => ({
        ...prev,
        contract_id: contract.contract_id,
        customer_id: customerId || 0,
        // Only set billing period defaults when creating, do not override edit values
        billing_period_start: prev.billing_period_start || period.start,
        billing_period_end: prev.billing_period_end || period.end
      }));
    }
  }, [contract, computeBillingPeriod]);

  // Adjust due date when contract or billing period start changes unless user edited due date
  useEffect(() => {
    if (!userEditedDueDate) {
      let newDue = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      if (contract?.currency === 'BRL' && formData.billing_period_start) {
        newDue = formData.billing_period_start;
      }
      setFormData(prev => (prev.due_date !== newDue ? { ...prev, due_date: newDue } : prev));
    }
  }, [contract, formData.billing_period_start, userEditedDueDate]);

  const [contractLines, setContractLines] = useState<ContractLine[]>([]);
  const [selectedLines, setSelectedLines] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [editableLines, setEditableLines] = useState<InvoiceLineEdit[]>([]);
  const [showAdvancedPricing, setShowAdvancedPricing] = useState(false);

  // Fetch contract lines when contract_id changes
  useEffect(() => {
    if (formData.contract_id && !isEditMode) {
      fetchContractLines(formData.contract_id);
    }
  }, [formData.contract_id, isEditMode]);

  // Fetch invoice lines in edit mode
  const fetchInvoiceLines = useCallback(async (invoiceId: string) => {
    try {
      setLoading(true);
      const invoice = await apiService.getInvoice(invoiceId);
      if (invoice.invoice_lines && Array.isArray(invoice.invoice_lines)) {
        const lines: InvoiceLineEdit[] = invoice.invoice_lines.map((line: any) => ({
          invoiceline_id: line.invoiceline_id,
          description: line.invoiceline_name || line.description || '',
          quantity: parseFloat(line.quantity) || 0,
          unit_price: parseFloat(line.unit_price) || 0,
          line_total: parseFloat(line.line_total) || 0,
          tax_rate: parseFloat(line.tax_rate) || 0,
          tax_amount: parseFloat(line.tax_amount) || 0
        }));
        setEditableLines(lines);
        calculateTotals(lines);
      }
    } catch (error) {
      console.error('Error fetching invoice lines:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isEditMode && initialData?.invoice_id) {
      fetchInvoiceLines(initialData.invoice_id);
    }
  }, [isEditMode, initialData, fetchInvoiceLines]);

  const fetchContractLines = async (contractId: string) => {
    try {
      setLoading(true);
      const lines = await apiService.getContractLines(contractId);
      setContractLines(lines);
      // Select all lines by default
      setSelectedLines(lines.map((line: ContractLine) => line.contractline_id));
      
      // Use the contract lines' upcoming billing dates if available
      if (lines.length > 0 && !isEditMode) {
        // Find the earliest upcoming_billing_start and latest upcoming_billing_end
        let earliestStart: string | null = null;
        let latestEnd: string | null = null;
        
        lines.forEach((line: ContractLine) => {
          if (line.upcoming_billing_start) {
            if (!earliestStart || line.upcoming_billing_start < earliestStart) {
              earliestStart = line.upcoming_billing_start;
            }
          }
          if (line.upcoming_billing_end) {
            if (!latestEnd || line.upcoming_billing_end > latestEnd) {
              latestEnd = line.upcoming_billing_end;
            }
          }
        });
        
        // Update billing period with contract lines' upcoming dates
        if (earliestStart && latestEnd) {
          // Store in const variables to satisfy TypeScript's null checking
          const startDate = earliestStart;
          const endDate = latestEnd;
          
          setFormData(prev => ({
            ...prev,
            billing_period_start: new Date(startDate).toISOString().split('T')[0],
            billing_period_end: new Date(endDate).toISOString().split('T')[0]
          }));
          
          // Update due date for BRL currency
          if (!userEditedDueDate) {
            const selectedContract = contracts.find((c: Contract) => c.contract_id === contractId);
            if (selectedContract?.currency === 'BRL') {
              setFormData(prev => ({
                ...prev,
                due_date: new Date(startDate).toISOString().split('T')[0]
              }));
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching contract lines:', error);
    } finally {
      setLoading(false);
    }
  };

  
  const calculateTotals = (lines: InvoiceLineEdit[]) => {
    const subtotal = lines.reduce((sum, line) => sum + (Number(line.line_total) || 0), 0);
    const taxAmount = lines.reduce((sum, line) => sum + (Number(line.tax_amount) || 0), 0);
    const total = subtotal + taxAmount;
    
    // Update form data with calculated totals
    setFormData(prev => ({
      ...prev,
      subtotal,
      tax_amount: taxAmount,
      total_amount: total
    }));
  };

  const handleLineChange = (index: number, field: keyof InvoiceLineEdit, value: any) => {
    const updatedLines = [...editableLines];
    updatedLines[index] = {
      ...updatedLines[index],
      [field]: value
    };

    // Recalculate line total and tax amount
    if (field === 'quantity' || field === 'unit_price' || field === 'tax_rate') {
      const line = updatedLines[index];
      line.line_total = (Number(line.quantity) || 0) * (Number(line.unit_price) || 0);
      line.tax_amount = line.line_total * ((Number(line.tax_rate) || 0) / 100);
    }

    setEditableLines(updatedLines);
    calculateTotals(updatedLines);
  };

  const handleDiscountChange = (field: 'discount_type' | 'discount_value', value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Calculate discount amount
      if (updated.subtotal) {
        if (updated.discount_type === 'percentage') {
          updated.discount_amount = (updated.subtotal * updated.discount_value) / 100;
        } else {
          updated.discount_amount = updated.discount_value;
        }
      }
      
      return updated;
    });
  };

  const addNewLine = () => {
    const newLine: InvoiceLineEdit = {
      description: '',
      quantity: 1,
      unit_price: 0,
      line_total: 0,
      tax_rate: 19, // Default tax rate
      tax_amount: 0
    };
    setEditableLines([...editableLines, newLine]);
  };

  const removeLine = (index: number) => {
    const updatedLines = editableLines.filter((_, i) => i !== index);
    setEditableLines(updatedLines);
    calculateTotals(updatedLines);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
               name.includes('_id') ? (value ? Number(value) : 0) : value
    }));

    // Update customer when contract changes
    if (name === 'contract_id' && value) {
      const selectedContract = contracts.find((c: Contract) => c.contract_id === value);
      if (selectedContract) {
        // Ensure customer_id is a valid number
        const customerId = selectedContract.customer_id;
        if (!customerId || (typeof customerId === 'number' && customerId <= 0)) {
          console.error('Contract has invalid customer_id:', selectedContract);
          alert('Selected contract does not have a valid customer. Please choose another contract.');
          return;
        }
        const period = computeBillingPeriod(selectedContract);
        setFormData(prev => ({
          ...prev,
          customer_id: customerId,
          // Always reset billing period when changing contract in create flow
          billing_period_start: isEditMode ? prev.billing_period_start : period.start,
          billing_period_end: isEditMode ? prev.billing_period_end : period.end
        }));
        // Reset due date default when contract changes unless user edited it
        if (!userEditedDueDate) {
          const defaultDue = selectedContract.currency === 'BRL' ? period.start : new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
          setFormData(prev => ({ ...prev, due_date: defaultDue }));
        }
      }
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleLineToggle = (lineId: string) => {
    setSelectedLines(prev => {
      if (prev.includes(lineId)) {
        return prev.filter(id => id !== lineId);
      } else {
        return [...prev, lineId];
      }
    });
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    // Validate invoice name only when editing an existing invoice
    if (isEditMode) {
      if (!formData.invoice_name) {
        newErrors.invoice_name = 'Invoice name is required';
      } else if (!isValidDocumentNumber(formData.invoice_name)) {
        newErrors.invoice_name = `Invoice name must follow pattern PREFIX-YYYY-CompanyNumber######### (allowed prefixes: ${DOCUMENT_PREFIXES.join(', ')})`;
      }
    }

    if (!formData.contract_id) {
      newErrors.contract_id = 'Contract is required';
    }

    if (!formData.customer_id || formData.customer_id === 0) {
      newErrors.customer_id = 'Customer is required';
    }

    if (!formData.invoice_date) {
      newErrors.invoice_date = 'Invoice date is required';
    }

    if (!formData.due_date) {
      newErrors.due_date = 'Due date is required';
    }

    if (formData.due_date && formData.invoice_date && formData.due_date < formData.invoice_date) {
      newErrors.due_date = 'Due date must be after invoice date';
    }

    if (!formData.billing_period_start) {
      newErrors.billing_period_start = 'Billing period start is required';
    }

    if (!formData.billing_period_end) {
      newErrors.billing_period_end = 'Billing period end is required';
    }

    if (formData.billing_period_end && formData.billing_period_start && formData.billing_period_end < formData.billing_period_start) {
      newErrors.billing_period_end = 'Billing period end must be after start date';
    }

    if (formData.include_contract_lines && selectedLines.length === 0) {
      newErrors.contract_lines = 'Please select at least one contract line';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      // Ensure customer_id is a valid number
      let customerId = formData.customer_id;
      
      // Convert string to number if needed
      if (typeof customerId === 'string') {
        customerId = parseInt(customerId, 10);
      }
      
      // Validate that customer_id is a valid positive integer
      if (!customerId || isNaN(customerId) || customerId <= 0) {
        console.error('Invalid customer_id:', formData.customer_id);
        alert('Invalid customer ID. Please select a valid contract with a customer.');
        return;
      }
      
      // Helper function to format date without timezone conversion
      const formatDateForBackend = (dateString: string): string => {
        // The date string is already in YYYY-MM-DD format from the input field
        // We append T00:00:00 to make it a valid ISO string without timezone conversion
        return `${dateString}T00:00:00`;
      };

      const submitData: any = {
        invoice_name: formData.invoice_name || undefined,  // Let backend generate if not provided
        contract_id: formData.contract_id,
        customer_id: customerId,
        invoice_date: formatDateForBackend(formData.invoice_date),
        due_date: formatDateForBackend(formData.due_date),
        billing_period_start: formatDateForBackend(formData.billing_period_start),
        billing_period_end: formatDateForBackend(formData.billing_period_end),
        payment_terms: formData.payment_terms,
        notes: formData.notes || undefined,
        terms_and_conditions: undefined,
        service_description: undefined
        // Explicitly do NOT include invoice_status, sending_status, or payment_status
      };

      if (isEditMode) {
        // Include edited lines and discount information
        // Convert editableLines to the format expected by the backend
        submitData.invoice_lines = editableLines.map(line => ({
          invoiceline_id: line.invoiceline_id,
          description: line.description,
          quantity: line.quantity,
          unit_price: line.unit_price,
          line_total: line.line_total,
          tax_rate: line.tax_rate,
          tax_amount: line.tax_amount
        }));
        submitData.discount_type = formData.discount_type;
        submitData.discount_value = formData.discount_value;
        submitData.discount_amount = formData.discount_amount;
        submitData.subtotal = formData.subtotal;
        submitData.tax_amount = formData.tax_amount;
        submitData.total_amount = formData.total_amount - formData.discount_amount;
      } else {
        // Include selected contract lines for new invoices
        submitData.selected_lines = formData.include_contract_lines ? selectedLines : undefined;
      }
      
      console.log('InvoiceForm submitting data:', submitData);
      console.log('Customer ID type:', typeof submitData.customer_id);
      
      onSubmit(submitData);
    }
  };

  const getCustomerName = (customerId: number) => {
    const customer = customers.find((c: Customer) => c.id === customerId);
    return customer?.customer_name || 'Select a customer';
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label className="form-label">Contract *</label>
        <select
          name="contract_id"
          value={formData.contract_id}
          onChange={handleChange}
          className={`form-control ${errors.contract_id ? 'error' : ''}`}
          disabled={false}
        >
          <option value="">Select a contract</option>
          {contracts.map((c) => (
            <option key={c.contract_id} value={c.contract_id}>
              {c.contract_name} (#{c.contract_number})
            </option>
          ))}
        </select>
        {errors.contract_id && <div className="form-error">{errors.contract_id}</div>}
      </div>

      {/* Invoice Name Field - Only show for edit mode */}
      {isEditMode && (
        <div className="form-group">
          <label className="form-label">Invoice Name *</label>
          <input
            type="text"
            name="invoice_name"
            value={formData.invoice_name}
            onChange={handleChange}
            className={`form-control ${errors.invoice_name ? 'error' : ''}`}
            disabled={!isDraft}
            placeholder="ECB-2024-100000"
          />
          {errors.invoice_name && <div className="form-error">{errors.invoice_name}</div>}
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Customer</label>
        <input
          type="text"
          value={getCustomerName(formData.customer_id)}
          className="form-control"
          disabled
        />
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Invoice Date *</label>
            <input
              type="date"
              name="invoice_date"
              value={formData.invoice_date}
              onChange={handleChange}
              className={`form-control ${errors.invoice_date ? 'error' : ''}`}
              disabled={!isDraft}
            />
            {errors.invoice_date && <div className="form-error">{errors.invoice_date}</div>}
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Due Date *</label>
            <input
              type="date"
              name="due_date"
              value={formData.due_date}
              onChange={(e) => { setUserEditedDueDate(true); handleChange(e); }}
              className={`form-control ${errors.due_date ? 'error' : ''}`}
              disabled={isEditMode && !isDraft ? false : !isDraft}
            />
            {errors.due_date && <div className="form-error">{errors.due_date}</div>}
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Billing Period Start *</label>
            <input
              type="date"
              name="billing_period_start"
              value={formData.billing_period_start}
              onChange={handleChange}
              className="form-control"
              disabled={!isDraft}
            />
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Billing Period End *</label>
            <input
              type="date"
              name="billing_period_end"
              value={formData.billing_period_end}
              onChange={handleChange}
              className="form-control"
              disabled={!isDraft}
            />
          </div>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Payment Terms</label>
        <select
          name="payment_terms"
          value={formData.payment_terms}
          onChange={handleChange}
          className="form-control"
          disabled={!isDraft}
        >
          <option value="immediate">Due on Receipt</option>
          <option value="7 days">Net 7</option>
          <option value="15 days">Net 15</option>
          <option value="30 days">Net 30</option>
          <option value="45 days">Net 45</option>
          <option value="60 days">Net 60</option>
          <option value="90 days">Net 90</option>
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">
          <input
            type="checkbox"
            name="include_contract_lines"
            checked={formData.include_contract_lines}
            onChange={handleChange}
            style={{ marginRight: '8px' }}
            disabled={!isDraft}
          />
          Include Contract Lines
        </label>
      </div>

      {formData.include_contract_lines && formData.contract_id && (
        <div className="form-group">
          <label className="form-label">Select Contract Lines to Include</label>
          {loading ? (
            <div className="text-center py-3">Loading contract lines...</div>
          ) : contractLines.length === 0 ? (
            <div className="alert alert-warning">
              No contract lines found for this contract. Please add contract lines before creating an invoice.
            </div>
          ) : (
            <div className="contract-lines-selection">
              {contractLines.map((line) => (
                <div key={line.contractline_id} className="contract-line-item" style={{ 
                  padding: '10px', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px', 
                  marginBottom: '10px',
                  backgroundColor: selectedLines.includes(line.contractline_id) ? '#f0f8ff' : 'white'
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedLines.includes(line.contractline_id)}
                      onChange={() => handleLineToggle(line.contractline_id)}
                      style={{ marginRight: '10px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <strong>{line.contractline_name}</strong>
                      <div style={{ fontSize: '0.9em', color: '#666' }}>
                        Line #{line.contractline_number} • 
                        Billing Cycle: {line.billing_cycle} • 
                        {line.trial_period > 0 && `Trial: ${line.trial_period} days • `}
                        Status: {line.is_active ? 'Active' : 'Inactive'}
                      </div>
                    </div>
                  </label>
                </div>
              ))}
            </div>
          )}
          {errors.contract_lines && <div className="form-error">{errors.contract_lines}</div>}
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Notes</label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          className="form-control"
          rows={4}
          placeholder="Additional notes or comments for the invoice..."
          disabled={!isDraft}
        />
      </div>

      {isEditMode && (
        <>
          <div className="form-group">
            <label className="form-label">Invoice Lines</label>
            <div className="invoice-lines-editor">
              {/* Bulk Edit UI */}
              {isDraft && (
                <BulkEditInvoiceLines
                  editableLines={editableLines}
                  setEditableLines={setEditableLines}
                />
              )}
              <table className="table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Description</th>
                    <th style={{ width: '100px' }}>Quantity</th>
                    <th style={{ width: '120px' }}>Unit Price</th>
                    <th style={{ width: '100px' }}>Tax Rate %</th>
                    <th style={{ width: '120px' }}>Line Total</th>
                    <th style={{ width: '120px' }}>Tax Amount</th>
                    <th style={{ width: '50px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {editableLines.map((line, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          type="checkbox"
                          checked={!!line.selected}
                          onChange={e => {
                            const updated = [...editableLines];
                            updated[index] = { ...updated[index], selected: e.target.checked };
                            setEditableLines(updated);
                          }}
                          disabled={!isDraft}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={line.description}
                          onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                          className="form-control"
                          placeholder="Line description"
                          disabled={!isDraft}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={line.quantity}
                          onChange={(e) => handleLineChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                          className="form-control"
                          min="0"
                          step="1"
                          disabled={!isDraft}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={line.unit_price}
                          onChange={(e) => handleLineChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          className="form-control"
                          step="0.01"
                          disabled={!isDraft}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={line.tax_rate}
                          onChange={(e) => handleLineChange(index, 'tax_rate', parseFloat(e.target.value) || 0)}
                          className="form-control"
                          min="0"
                          max="100"
                          step="0.01"
                          disabled={!isDraft}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={Number(line.line_total || 0).toFixed(2)}
                          className="form-control"
                          disabled
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={Number(line.tax_amount || 0).toFixed(2)}
                          className="form-control"
                          disabled
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeLine(index)}
                          disabled={!isDraft}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={addNewLine}
                disabled={!isDraft}
              >
                + Add Line
              </button>
            </div>
          </div>


          <div className="form-group">
            <label className="form-label">
              <input
                type="checkbox"
                checked={showAdvancedPricing}
                onChange={(e) => setShowAdvancedPricing(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Advanced Pricing (Discounts)
            </label>
          </div>

          {showAdvancedPricing && (
            <div className="advanced-pricing-section" style={{ 
              padding: '15px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '4px',
              marginBottom: '20px'
            }}>
              <div className="form-row">
                <div className="form-col">
                  <div className="form-group">
                    <label className="form-label">Discount Type</label>
                    <select
                      value={formData.discount_type}
                      onChange={(e) => handleDiscountChange('discount_type', e.target.value)}
                      className="form-control"
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount</option>
                    </select>
                  </div>
                </div>
                <div className="form-col">
                  <div className="form-group">
                    <label className="form-label">
                      Discount {formData.discount_type === 'percentage' ? 'Percentage' : 'Amount'}
                    </label>
                    <input
                      type="number"
                      value={formData.discount_value}
                      onChange={(e) => handleDiscountChange('discount_value', parseFloat(e.target.value) || 0)}
                      className="form-control"
                      min="0"
                      max={formData.discount_type === 'percentage' ? "100" : undefined}
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              <div className="invoice-totals" style={{ marginTop: '20px' }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ marginBottom: '5px' }}>
                    <strong>Subtotal:</strong> {formData.subtotal?.toFixed(2) || '0.00'} {formData.currency || 'EUR'}
                  </div>
                  <div style={{ marginBottom: '5px' }}>
                    <strong>Tax Amount:</strong> {formData.tax_amount?.toFixed(2) || '0.00'} {formData.currency || 'EUR'}
                  </div>
                  <div style={{ marginBottom: '5px', color: '#dc3545' }}>
                    <strong>Discount:</strong> -{formData.discount_amount?.toFixed(2) || '0.00'} {formData.currency || 'EUR'}
                  </div>
                  <div style={{ fontSize: '1.2em', fontWeight: 'bold', borderTop: '2px solid #333', paddingTop: '5px' }}>
                    <strong>Total:</strong> {((formData.total_amount || 0) - (formData.discount_amount || 0)).toFixed(2)} {formData.currency || 'EUR'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          {isEditMode ? 'Update Invoice' : 'Create Invoice'}
        </button>
      </div>

      <style>{`
        /* Ensure all form controls appear enabled */
        .form-control {
          background-color: white !important;
          color: #212529 !important;
          opacity: 1 !important;
          cursor: text !important;
        }
        
        .invoice-lines-editor input {
          background-color: white !important;
          color: #212529 !important;
          opacity: 1 !important;
        }
        
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </form>
  );
};

export default InvoiceForm;